import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import {
  createFgaPolicyViewLink,
  createGroupDetailLink,
  createGroupPermissionLFLink,
  createRequestContextDetailLink,
  createTemplateParentDetailsLink,
  createWorkspaceDetailLink,
  createWorkspaceRequestLink,
  createWSPermissionLFLink,
} from 'src/routes';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { StatusIndicator } from '@amzn/awsui-components-react-v3/polaris';
import { isValidMidwayUser, isValidWorkspace } from 'src/commons/validationUtils';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Header,
  Icon,
  KeyValuePairs,
  Modal,
  SpaceBetween,
  Table,
  Textarea,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import {
  convertApprovalTemplateIDToLink,
  convertApprovalWorkflowIDToLink,
} from 'src/components/permissions/myPermissions/common';
import { StatusIcon } from 'src/components/permissions/myDatasets/statusIcon';
import { ResourceBasedReqRecord } from 'src/components/requests/browseResourceBasedRequests';
import { BadgeProps } from '@amzn/awsui-components-console';
import { APPROVALS_STATUS_DRAFT, PENDING_LOWERCASE } from 'src/commons/constants';
import { getPhoneToolLink } from 'src/commons/common';
import { GladstoneRequestItem } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import _ from 'lodash';

export const tagBasedColumnDefinition = (isIncomingRequests): TableProps.ColumnDefinition<any>[] => [
  {
    id: 'requestId',
    header: 'Request ID',
    cell: (item) =>
      item.requestId ? <Link href={createWorkspaceRequestLink(item.requestId)}>{item.requestId}</Link> : '',
    minWidth: 200,
  },
  {
    id: 'resourceTag',
    header: 'Resource tag',
    cell: (item) => item.resourceTag,
    minWidth: 200,
    sortingField: 'resourceTag',
  },
  isIncomingRequests
    ? {
        id: 'requestedWorkspace',
        header: 'Requested owner',
        cell: (item) => (
          <Link
            href={
              isValidWorkspace(item.requestedWorkspace)
                ? createWorkspaceDetailLink(item.requestedWorkspace)
                : createGroupDetailLink(item.requestedWorkspace)
            }
          >
            {item.requestedWorkspace}
          </Link>
        ),
        minWidth: 100,
        sortingField: 'requestedWorkspace',
      }
    : {
        id: 'requestedTo',
        header: 'Requested to owner',
        cell: (item) => (
          <Link
            href={
              isValidWorkspace(item.requestedTo)
                ? createWorkspaceDetailLink(item.requestedTo)
                : createGroupDetailLink(item.requestedTo)
            }
          >
            {item.requestedTo}
          </Link>
        ),
        minWidth: 100,
        sortingField: 'requestedTo',
      },
  {
    id: 'requestedBy',
    header: 'Requested by',
    cell: (item) => item.requestedBy,
    minWidth: 100,
    sortingField: 'requestedBy',
  },
  {
    id: 'requestedTime',
    header: 'Requested time',
    cell: (item) => item.requestedTime,
    minWidth: 100,
    sortingField: 'requestedTime',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => (
      <div>
        {item.status == 'PENDING' && <StatusIndicator type='pending'>Pending</StatusIndicator>}
        {item.status == 'APPROVED' && <StatusIndicator>Approved</StatusIndicator>}
        {item.status == 'DENIED' && <StatusIndicator type='error'>Denied</StatusIndicator>}
        {item.status == 'CANCELED' && <StatusIndicator type='stopped'>Canceled</StatusIndicator>}
        {item.status == 'RUNNING' && <StatusIndicator type='pending'>Running</StatusIndicator>}
        {item.status == 'FAILED' && <StatusIndicator type='error'>Failed</StatusIndicator>}
      </div>
    ),
    minWidth: 125,
    sortingField: 'status',
  },
  {
    id: 'statusReason',
    header: 'Status reason',
    cell: (item) => item.statusReason,
    minWidth: 125,
    sortingField: 'statusReason',
  },
];

export const resourceBasedColumnDefinition = (
  isIncomingRequests,
): TableProps.ColumnDefinition<ResourceBasedReqRecord>[] => [
  {
    id: 'requestId',
    header: isIncomingRequests ? 'Group/Request ID' : 'Request ID',
    cell: (item) => {
      return isIncomingRequests ? (
        item.groupId ? (
          item.groupId
        ) : item.requestId ? (
          <Link external href={createWorkspaceRequestLink(item.requestId)}>
            {item.requestId}
          </Link>
        ) : (
          ''
        )
      ) : item.requestId ? (
        <Link external href={createWorkspaceRequestLink(item.requestId)}>
          {item.requestId}
        </Link>
      ) : (
        ''
      );
    },
    minWidth: 200,
  },
  {
    id: 'requestedBy',
    header: 'Requested by',
    cell: (item) => item.requestedBy,
    minWidth: 100,
    sortingField: 'requestedBy',
  },
  {
    id: 'requestedTime',
    header: 'Requested time',
    cell: (item) => item.requestedTime,
    minWidth: 200,
    sortingField: 'requestedTime',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => (
      <div>
        {item.status == 'PENDING' && <StatusIndicator type='pending'>Pending</StatusIndicator>}
        {item.status == 'RUNNING' && <StatusIndicator type='pending'>Running</StatusIndicator>}
        {item.status == 'APPROVED' && <StatusIndicator>Approved</StatusIndicator>}
        {item.status == 'DENIED' && <StatusIndicator type='error'>Denied</StatusIndicator>}
        {item.status == 'FAILED' && <StatusIndicator type='error'>Failed</StatusIndicator>}
        {item.status == 'CANCELED' && <StatusIndicator type='stopped'>Canceled</StatusIndicator>}
      </div>
    ),
    minWidth: 125,
    sortingField: 'status',
  },
  {
    id: 'statusReason',
    header: 'Status reason',
    cell: (item) => item.statusReason,
    minWidth: 150,
    sortingField: 'statusReason',
  },
  {
    id: 'resource',
    header: 'Resource',
    cell: (item) => {
      if (item.fgapId && item.schemaName)
        return (
          <Link
            external
            href={createFgaPolicyViewLink(item.fgapId)}
          >{`${item.catalogId}.${item.databaseName}.${item.schemaName}.${item.tableName}.${item.fgapName}`}</Link>
        );
      else if (item.fgapId)
        return (
          <Link
            external
            href={createFgaPolicyViewLink(item.fgapId)}
          >{`${item.catalogId}.${item.databaseName}.${item.tableName}.${item.fgapName}`}</Link>
        );
      else if (item.schemaName) return `${item.catalogId}.${item.databaseName}.${item.schemaName}.${item.tableName}`;
      else if (item.tableName) return `${item.catalogId}.${item.databaseName}.${item.tableName}`;
      else if (item.databaseName) return `${item.catalogId}.${item.databaseName}`;
      else return '';
    },
    minWidth: 300,
    sortingField: 'catalogId',
  },
  {
    id: 'approvalWflId',
    header: 'Approval workflow',
    cell: (item) => {
      if (item.approvalsWorkflow && item.approvalsWorkflow?.[0]?.approvalWorkflowId)
        return (
          <Link external href={convertApprovalWorkflowIDToLink(item.approvalsWorkflow[0].approvalWorkflowId)}>
            {item.approvalsWorkflow[0].approvalWorkflowId}
          </Link>
        );
      else return 'N/A';
    },
    minWidth: 100,
    sortingComparator: (a, b) => {
      if (
        a.approvalsWorkflow &&
        a.approvalsWorkflow?.[0]?.approvalWorkflowId &&
        b.approvalsWorkflow &&
        b.approvalsWorkflow?.[0]?.approvalWorkflowId
      )
        return a.approvalsWorkflow[0].approvalWorkflowId.localeCompare(b.approvalsWorkflow[0].approvalWorkflowId);
      else return 0;
    },
  },
  {
    id: 'reqContextId',
    header: 'Request context',
    cell: (item) => {
      if (item.requestContextIds && item.requestContextIds.length == 1)
        return (
          <Link external href={createRequestContextDetailLink(item.requestContextIds[0])}>
            {item.requestContextIds[0]}
          </Link>
        );
      else return 'N/A';
    },
    minWidth: 100,
  },
  isIncomingRequests
    ? {
        id: 'requestedWorkspace',
        header: 'Requested owner',
        cell: (item) => (
          <Link
            external
            href={
              isValidWorkspace(item.requestedOwnerId)
                ? createWorkspaceDetailLink(item.requestedOwnerId)
                : createGroupDetailLink(item.requestedOwnerId)
            }
          >
            {item.requestedOwnerId}
          </Link>
        ),
        minWidth: 100,
        sortingField: 'requestedOwnerId',
      }
    : {
        id: 'requestedTo',
        header: 'Requested to owner',
        cell: (item) => (
          <Link
            external
            href={
              isValidWorkspace(item.requestedTo)
                ? createWorkspaceDetailLink(item.requestedTo)
                : createGroupDetailLink(item.requestedTo)
            }
          >
            {item.requestedTo}
          </Link>
        ),
        minWidth: 100,
        sortingField: 'requestedTo',
      },
  {
    id: 'permissionType',
    header: 'Permission type',
    cell: (item) => item.permissionType,
    minWidth: 50,
    sortingField: 'permissionType',
  },
];

const getRiskScoreLevel = (riskScore: number) => {
  let risk = 'unknown';
  let riskColor: BadgeProps['color'] = 'grey';
  if (riskScore >= 70) {
    risk = 'High';
    riskColor = 'severity-critical';
  } else if (riskScore >= 40) {
    risk = 'Medium';
    riskColor = 'severity-medium';
  } else if (riskScore >= 10) {
    risk = 'Low';
    riskColor = 'severity-low';
  } else if (riskScore >= 0) {
    risk = 'Very low';
    riskColor = 'green';
  }
  return <Badge color={riskColor}>{risk}</Badge>;
};

const requestAgenticAnalysisTable = (analysis) => {
  return (
    <Container
      header={
        <Header variant={'h2'}>
          Agentic analysis <Icon name={'gen-ai'} />
        </Header>
      }
    >
      <KeyValuePairs
        columns={4}
        items={[
          analysis?.riskScore !== undefined
            ? {
                label: 'Risk',
                value: getRiskScoreLevel(analysis?.riskScore),
              }
            : undefined,
          analysis?.riskScoreFactors?.length > 0
            ? {
                label: 'Risk factors',
                value: (
                  <TextContent>
                    <ul>
                      {analysis.riskScoreFactors.map((risk, idx) => (
                        <li key={idx}>{risk}</li>
                      ))}
                    </ul>
                  </TextContent>
                ),
              }
            : undefined,
          analysis?.recommendation ? { label: 'Recommendation', value: analysis.recommendation } : undefined,
        ].filter((item) => item != undefined)}
      />
    </Container>
  );
};

export const ResourceBasedRequestInformation = ({ request }) => (
  <SpaceBetween size={'m'} direction={'vertical'}>
    <Container header={<Header variant={'h2'}>Request details</Header>}>
      <KeyValuePairs
        columns={4}
        items={[
          { label: 'Requested by', value: request.requestedBy },
          request.requestWorkspace
            ? {
                label: 'Requested workspace',
                value: (
                  <Link
                    href={
                      isValidWorkspace(request.requestWorkspace)
                        ? createWorkspaceDetailLink(request.requestWorkspace)
                        : createGroupDetailLink(request.requestWorkspace)
                    }
                  >
                    {request.requestWorkspace}
                  </Link>
                ),
              }
            : undefined,
          request.decidedBy ? { label: 'Decided by', value: request.decidedBy } : undefined,
          request.owner
            ? {
                label: 'Data owner',
                value: (
                  <Link
                    href={
                      isValidWorkspace(request.owner)
                        ? createWorkspaceDetailLink(request.owner)
                        : createGroupDetailLink(request.owner)
                    }
                  >
                    {request.owner}
                  </Link>
                ),
              }
            : undefined,
          { label: 'Status', value: StatusIcon({ status: request.status }) },
          request.statusReason ? { label: 'Status reason', value: request.statusReason } : undefined,
          // only defined for denied status.
          request.reasonOfAction ? { label: 'Status reason', value: request.reasonOfAction } : undefined,
          { label: 'Request date', value: request.timeOfRequest },
          request.timeOfDecision ? { label: 'Decision date', value: request.timeOfDecision } : undefined,
          { label: 'External account', value: request.principal },
          { label: 'Catalog ID', value: request.catalogId },
          request.clusterId ? { label: 'Cluster ID', value: request.clusterId } : undefined,
          { label: 'Database name', value: request.databaseName },
          request.schemaName ? { label: 'Schema name', value: request.schemaName } : undefined,
          { label: 'Table name', value: request.tableName },
          request.fgapName ? { label: 'Fine grain policy', value: request.fgapName } : undefined,
          request.columnNames
            ? {
                label: `Columns${request.columnWildCard ? ` (${request.columnWildCard})` : ``}`,
                value: request.columnNames,
              }
            : undefined,
          request.redshiftRole ? { label: 'Redshift role', value: request.redshiftRole } : undefined,
          request.consumerPrincipal ? { label: 'Consumer principal', value: request.consumerPrincipal } : undefined,
          request.columnWildCard ? { label: 'Column wildcard', value: request.columnWildCard } : undefined,
          { label: 'Permissions', value: request.permissions },
          { label: 'Grantable permissions', value: request.grantablePermissions },
          { label: 'Use case', value: request.useCase ? request.useCase : ' ' },
          request.dataConsumerType ? { label: 'Data consumer type', value: request.dataConsumerType } : undefined,
          request.reasonOfAction ? { label: 'Reason for action', value: request.reasonOfAction } : undefined,
          request.approvedPermissionId
            ? {
                label: 'Approved data permission',
                value: (
                  <Link
                    href={
                      isValidWorkspace(request.owner)
                        ? createWSPermissionLFLink(request.approvedPermissionId)
                        : createGroupPermissionLFLink(request.approvedPermissionId)
                    }
                  >
                    {request.approvedPermissionId}
                  </Link>
                ),
              }
            : undefined,
          request.simTickets?.length > 0
            ? {
                label: 'SIM ticket links',
                value: (
                  <div>
                    {request.simTickets.map((st) => {
                      return (
                        <>
                          <Link href={st?.simTicketLink}>{st?.simTemplateName}</Link> <br />
                        </>
                      );
                    })}
                  </div>
                ),
              }
            : undefined,
          request.requestContextIds?.length > 0
            ? {
                label: 'Data usage agreements (Request context IDs)',
                value: (
                  <div>
                    {request.requestContextIds.map((rcId) => {
                      return (
                        <>
                          <Link href={createRequestContextDetailLink(rcId)}>{rcId}</Link> <br />
                        </>
                      );
                    })}
                  </div>
                ),
              }
            : undefined,
        ].filter((item) => item != undefined)}
      />
    </Container>
    {request.agenticAnalysisInfo?.agenticAnalysisStatus === 'Success' &&
      requestAgenticAnalysisTable(request.agenticAnalysisInfo)}
    {request.approvalsWorkflow?.length > 0 && requestApprovalsWorkflowInfoTable(request)}
  </SpaceBetween>
);

export interface ApprovalWorkflowPendingSubmissionModalProps {
  approvalWorkflowPendingLinks: string[];
  approvalWorkflowModalVisible: boolean;
  setApprovalWorkflowModalVisible: any;
}

export const ApprovalWorkflowPendingSubmissionModal = (props: ApprovalWorkflowPendingSubmissionModalProps) => {
  return (
    <Modal
      onDismiss={() => props.setApprovalWorkflowModalVisible(false)}
      visible={props.approvalWorkflowModalVisible}
      size='medium'
      footer={
        <Box float='right'>
          <Button variant='primary' onClick={() => props.setApprovalWorkflowModalVisible(false)}>
            Confirm
          </Button>
        </Box>
      }
      header='Pending Approval Workflow submission'
    >
      <TextContent>
        As part of the access request process, some approval workflows were created in Amazon Approvals and action is
        required. To finalize the request, navigate to the Approval links below and fill out the remaining details by
        editing the summary under Basic Details and Start the approval.
        <br />
        <ul>
          {props.approvalWorkflowPendingLinks.map((link, idx) => (
            <li key={'approvalIndex' + idx}>
              <Link href={link} external={true}>
                {link.includes('/') ? link.split('/').pop() : 'Approval workflow link'}
              </Link>
            </li>
          ))}
        </ul>
        <br />
      </TextContent>
      <Alert i18nStrings={{ warningIconAriaLabel: 'warning' }} type='warning'>
        {' '}
        The request will be in pending and not processed until then!{' '}
      </Alert>
    </Modal>
  );
};

export const requestApprovalsWorkflowInfoTable = (request) => {
  const [approvalWorkflowModalVisible, setApprovalWorkflowModalVisible] = useState(false);
  const [approvalWorkflowPendingLinks, setApprovalWorkflowPendingLinks] = useState([]);
  const approvalInfoObjects = deDupeApprovalsInfoByApprovalWorkflowId(request?.approvalsWorkflow);
  useEffect(() => {
    if (
      request['status']?.toLowerCase() === PENDING_LOWERCASE &&
      approvalInfoObjects.some((a) => a['approvalWorkflowStatus'] === APPROVALS_STATUS_DRAFT)
    ) {
      const approvalWorkflowPendingLinksAry = approvalInfoObjects
        .filter((a) => a['approvalWorkflowStatus'] === APPROVALS_STATUS_DRAFT)
        .map((a) => convertApprovalWorkflowIDToLink(a.approvalWorkflowId));
      setApprovalWorkflowPendingLinks(approvalWorkflowPendingLinksAry);
      setApprovalWorkflowModalVisible(true);
    }
  }, [request]);

  return (
    <>
      <ApprovalWorkflowPendingSubmissionModal
        approvalWorkflowPendingLinks={approvalWorkflowPendingLinks}
        approvalWorkflowModalVisible={approvalWorkflowModalVisible}
        setApprovalWorkflowModalVisible={setApprovalWorkflowModalVisible}
      />
      <Table
        columnDefinitions={[
          {
            id: 'approvalTemplateId',
            header: 'Approval template ID',
            cell: (e) => (
              <Link href={convertApprovalTemplateIDToLink(e['approvalTemplateId'])} external={true}>
                {e['approvalTemplateId']}
              </Link>
            ),
          },
          {
            id: 'approvalWorkflowId',
            header: 'Approval workflow ID',
            cell: (e) => (
              <Link href={convertApprovalWorkflowIDToLink(e['approvalWorkflowId'])} external={true}>
                {e['approvalWorkflowId']}
              </Link>
            ),
          },
          {
            id: 'approvalWorkflowStatus',
            header: 'Approval workflow status',
            cell: (e) => e['approvalWorkflowStatus'],
          },
          {
            id: 'approvalDecisionTime',
            header: 'Approval decision time',
            cell: (e) => e['approvalDecisionTime'],
          },
          {
            id: 'omniTemplate',
            header: 'Omni template',
            cell: (e) =>
              e['omniTemplateId'] != undefined ? (
                <Link href={createTemplateParentDetailsLink(e['omniTemplateId'])} external={true}>
                  {e['omniTemplateId']}
                </Link>
              ) : (
                '-'
              ),
          },
        ]}
        items={approvalInfoObjects}
        loadingText='Loading resources'
        header={<Header>Approval workflow details</Header>}
      />
    </>
  );
};

export const deDupeApprovalsInfoByApprovalWorkflowId = (approvalsWorkflow) => {
  let uniqueApprovalWorkflowIds = new Set();
  let uniqueApprovalInfos = [];
  approvalsWorkflow?.forEach((approvalsInfo) => {
    if (!uniqueApprovalWorkflowIds.has(approvalsInfo.approvalWorkflowId)) {
      uniqueApprovalWorkflowIds.add(approvalsInfo.approvalWorkflowId);
      uniqueApprovalInfos.push(approvalsInfo);
    }
  });
  return uniqueApprovalInfos;
};

export const TagBasedRequestInformation = ({ request }) => (
  <SpaceBetween size={'m'} direction={'vertical'}>
    <Container header={<Header variant={'h2'}>Request details</Header>}>
      <KeyValuePairs
        columns={4}
        items={[
          { label: 'Requested by', value: request.requestedBy },
          request.requestWorkspace
            ? {
                label: 'Requested workspace',
                value: (
                  <Link
                    href={
                      isValidWorkspace(request.requestWorkspace)
                        ? createWorkspaceDetailLink(request.requestWorkspace)
                        : createGroupDetailLink(request.requestWorkspace)
                    }
                  >
                    {request.requestWorkspace}
                  </Link>
                ),
              }
            : undefined,
          request.decidedBy ? { label: 'Decided by', value: request.decidedBy } : undefined,
          request.owner
            ? {
                label: 'Data owner',
                value: (
                  <Link
                    href={
                      isValidWorkspace(request.owner)
                        ? createWorkspaceDetailLink(request.owner)
                        : createGroupDetailLink(request.owner)
                    }
                  >
                    {request.owner}
                  </Link>
                ),
              }
            : undefined,
          { label: 'Status', value: StatusIcon({ status: request.status }) },
          request.statusReason ? { label: 'Status reason', value: request.statusReason } : undefined,
          { label: 'Request date', value: request.timeOfRequest },
          request.timeOfDecision ? { label: 'Decision date', value: request.timeOfDecision } : undefined,
          { label: 'External account', value: request.principal },
          { label: 'Resource tag', value: request.resourceTag },
          { label: 'Permissions', value: request.permissions },
          { label: 'Grantable permissions', value: request.grantablePermissions },
          request.useCase ? { label: 'Use case', value: request.useCase } : undefined,
          request.dataConsumerType ? { label: 'Data consumer type', value: request.dataConsumerType } : undefined,
          request.reasonOfAction ? { label: 'Reason for action', value: request.reasonOfAction } : undefined,
          request.approvedPermissionId
            ? {
                label: 'Approved data permission',
                value: (
                  <Link
                    href={
                      isValidWorkspace(request.owner)
                        ? createWSPermissionLFLink(request.approvedPermissionId)
                        : createGroupPermissionLFLink(request.approvedPermissionId)
                    }
                  >
                    {request.approvedPermissionId}
                  </Link>
                ),
              }
            : undefined,
          request.simTickets?.length > 0
            ? {
                label: 'SIM ticket links',
                value: (
                  <div>
                    {request.simTickets.map((st) => {
                      return (
                        <>
                          <Link href={st?.simTicketLink}>{st?.simTemplateName}</Link> <br />
                        </>
                      );
                    })}
                  </div>
                ),
              }
            : undefined,
        ].filter((item) => item != undefined)}
      />
    </Container>
    {request.agenticAnalysisInfo && requestAgenticAnalysisTable(request.agenticAnalysisInfo)}
    {request.approvalsWorkflow?.length > 0 && requestApprovalsWorkflowInfoTable(request)}
  </SpaceBetween>
);

interface DenyOrCancelModalProps {
  modalVisible: boolean;
  action: string;
  closeModal: any;
  buttonLoading: boolean;
  submitAction: any;
  approvalWorkflowStatusOutOfSync: boolean;
  textAreaPlaceholder: string;
  reasonOfAction: string;
  setReasonOfAction: any;
}

export const DenyOrCancelModal = (props: DenyOrCancelModalProps) => {
  const [isInvalid, setIsInvalid] = useState(true);

  return (
    <Modal
      visible={props.modalVisible}
      header={props.action === 'cancel' ? 'Enter reason for canceling' : 'Enter reason for denying'}
      onDismiss={() => props.closeModal()}
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={() => props.closeModal()}>
              No
            </Button>
            <Button variant='primary' loading={props.buttonLoading} onClick={() => props.submitAction(props.action)}>
              Yes
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {props.approvalWorkflowStatusOutOfSync && props.action == 'deny' && (
        <div>
          {' '}
          <Alert type='warning'>
            The approval workflow associated with this request has been approved or pending. Make sure you want to deny
            this request.
          </Alert>
          <br />
        </div>
      )}
      <Textarea
        value={props.reasonOfAction}
        onChange={({ detail }) => {
          props.setReasonOfAction(detail.value);
          setIsInvalid(!props.reasonOfAction || props.reasonOfAction.length == 0);
        }}
        placeholder={props.textAreaPlaceholder}
        invalid={isInvalid}
      />
      Are you sure you want to {props.action} this request?
    </Modal>
  );
};

export const iamColumnDefinition = (isIncomingRequests: boolean): TableProps.ColumnDefinition<any>[] => [
  {
    id: 'catalogId',
    header: 'Catalog ID',
    cell: (item) => item.catalogId,
    minWidth: 125,
    sortingField: 'catalogId',
  },
  {
    id: 'databaseName',
    header: 'Database name',
    cell: (item) => item.databaseName,
    minWidth: 200,
    sortingField: 'databaseName',
  },
  {
    id: 'tableName',
    header: 'Table name',
    cell: (item) => item.tableName,
    minWidth: 200,
    sortingField: 'tableName',
  },
  isIncomingRequests
    ? {
        id: 'requestedBy',
        header: 'Requested by',
        cell: (item) => item.groupRequestedBy,
        minWidth: 100,
        sortingField: 'groupRequestedBy',
      }
    : {
        id: 'requestedTo',
        header: 'Requested to',
        cell: (item) => item.groupRequestedTo,
        minWidth: 100,
        sortingField: 'groupRequestedTo',
      },
  {
    id: 'requestedTime',
    header: 'Requested time',
    cell: (item) => item.timeOfRequest,
    minWidth: 100,
    sortingField: 'timeOfRequest',
  },
  {
    id: 'requester',
    header: 'Requester',
    cell: (item) => (isValidMidwayUser(item.requester) ? getPhoneToolLink(item.requester) : item.requester),
    minWidth: 100,
    sortingField: 'requester',
  },
];

export const extractAccessManagementInfoFromRequest = (item: GladstoneRequestItem, transform) => {
  let allSimTicketLinks = [];
  let uniqueApprovalWorkflowIds = new Set<string>();
  let approvals = [];
  let uniqueRequestContextIds = new Set<string>();
  let requestContextIds = [];
  if (item.additionalMetadata != undefined && !_.isEmpty(item.additionalMetadata.accessManagementInfoList)) {
    for (const accessManagementInfo of item.additionalMetadata.accessManagementInfoList) {
      if (accessManagementInfo.simTicketLinks) {
        allSimTicketLinks = allSimTicketLinks.concat(
          accessManagementInfo?.simTicketLinks?.map((simTemplate) => {
            return {
              simTemplateLink: simTemplate.simTemplateLink,
              simTemplateName: simTemplate.simTemplateName,
              simTicketLink: simTemplate.simTicketLink,
            };
          }),
        );
      }
      if (
        accessManagementInfo.approvalTemplateId &&
        !uniqueApprovalWorkflowIds.has(accessManagementInfo.approvalWorkflowId)
      ) {
        uniqueApprovalWorkflowIds.add(accessManagementInfo.approvalWorkflowId);
        approvals = approvals.concat({
          approvalTemplateId: accessManagementInfo.approvalTemplateId,
          approvalWorkflowId: accessManagementInfo.approvalWorkflowId,
          approvalWorkflowStatus: accessManagementInfo.approvalWorkflowStatus,
          approvalDecisionTime: accessManagementInfo.approvalDecisionTime,
          omniTemplateId: accessManagementInfo.templateId,
          omniResourceId: accessManagementInfo.resourceId,
        });
      }
    }
  }
  if (item.additionalMetadata != undefined && !_.isEmpty(item.additionalMetadata.accessRequestWorkflowInfoList)) {
    for (const arw of item.additionalMetadata.accessRequestWorkflowInfoList) {
      if (!!arw.approvalTemplateId && !uniqueApprovalWorkflowIds.has(arw.approvalWorkflowId)) {
        uniqueApprovalWorkflowIds.add(arw.approvalWorkflowId);
        approvals = approvals.concat({
          approvalTemplateId: arw.approvalTemplateId,
          approvalWorkflowId: arw.approvalWorkflowId,
          approvalWorkflowStatus: arw.approvalWorkflowStatus,
          approvalDecisionTime: arw.approvalDecisionTime,
          omniTemplateId: arw.templateId,
          omniResourceId: arw.resourceId,
          omniTemplateType: arw.templateType,
        });
      }
      if (!!arw.requestContextId && !uniqueRequestContextIds.has(arw.requestContextId)) {
        uniqueRequestContextIds.add(arw.requestContextId);
        requestContextIds.push(arw.requestContextId);
      }
    }
  }
  if (allSimTicketLinks.length > 0) {
    transform['simTickets'] = allSimTicketLinks;
  }
  if (approvals.length > 0) {
    transform['approvalsWorkflow'] = approvals;
  }
  if (requestContextIds.length > 0) {
    transform['requestContextIds'] = requestContextIds;
  }
  if (item.additionalMetadata != undefined && item.additionalMetadata.dataZoneMetadata != undefined) {
    transform['dataZoneAssetId'] = item.additionalMetadata.dataZoneMetadata.assetId;
    transform['dataZoneProjectId'] = item.additionalMetadata.dataZoneMetadata.projectId;
  }
  return transform;
};
